import { z } from "zod";

import { fails, reference, text } from "@frontend/lib/form/validators";
import { msg } from "@lingui/core/macro";

/** The column the price lands in holds whole francs, and rounds the rest. */
const AMOUNT = /^\d+$/;

/** A price left empty is a line that follows the house price. */
const grantedPrice = z
  .string()
  .refine(
    (value) => "" === value.trim() || AMOUNT.test(value.replace(/\s/gu, "")),
    fails(msg`Enter a whole number of francs`),
  )
  .nullable();

export const priceSchema = z.object({
  priceTypeId: z.number().int(),
  price: grantedPrice.default(null),
});

export const conditionsSchema = z
  .object({
    lockVersion: z.string().nullable().default(null),
    paymentTypeId: reference.default(null),
    paymentEmail: z.string().nullable().default(null),
    paymentEmailCopyTo: z.string().nullable().default(null),
    paymentTargetId: reference.default(null),
    subscriptionPaymentPeriodId: reference.default(null),
    interventionPaymentPeriodId: reference.default(null),
    subscriptionBillingTimeId: reference.default(null),
    riskClassId: reference.default(null),
    ongoingAuthorized: z.number().int().nonnegative().nullable().default(null),
    priceRevisionActivation: z.boolean().default(true),
    prices: z.array(priceSchema).default([]),
    /** Which payment types ask for an address, so the check knows. */
    emailRequiredFor: z.array(z.number().int()).default([]),
  })
  .superRefine((values, ctx) => {
    const asked = values.emailRequiredFor.includes(values.paymentTypeId ?? -1);

    if (asked && "" === (values.paymentEmail ?? "").trim()) {
      ctx.addIssue({
        code: "custom",
        message: text(msg`This payment type is billed by email`),
        path: ["paymentEmail"],
      });
    }
  });

export type PriceValues = z.infer<typeof priceSchema>;
export type ConditionsValues = z.infer<typeof conditionsSchema>;
